import { useEffect, useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Link, { TransitionNavLink as NavLink } from '@/components/base/TransitionLink';
import { jumpTo } from '@/components/feature/smoothScroll';
import { getHeroActive, subscribeHeroActive } from '@/hooks/heroState';

const links = [
  { to: '/services', label: 'Services' },
  { to: '/projects', label: 'Projects' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const { pathname, hash } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [heroActive, setHeroActiveState] = useState(getHeroActive());
  // While the pinned Hero sequence runs, keep the Hero header look regardless of scrollY.
  const overlay = pathname === '/' && (heroActive || !scrolled) && !open;

  useEffect(() => subscribeHeroActive(setHeroActiveState), []);

  // Single scroll authority: decide the final position once, before first paint.
  useLayoutEffect(() => {
    const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
    jumpTo(target ?? 0);
    setOpen(false);
  }, [pathname, hash]);

  useEffect(() => {
    // Home: stay transparent until the whole first screen (hero) has scrolled away.
    const threshold = () => (pathname === '/' ? Math.max(window.innerHeight, 560) - 64 : 40);
    const onScroll = () => setScrolled(window.scrollY > threshold());
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [pathname]);

  const tone = overlay ? 'text-background-50' : 'text-foreground-950';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        overlay ? 'bg-transparent' : 'bg-background-50 border-b border-background-300/60'
      }`}
    >
      <div className={`shell flex items-center justify-between h-16 ${tone}`}>
        <Link to="/" className="flex items-baseline gap-2 cursor-pointer" aria-label="MATERA home">
          <span className="font-heading font-bold tracking-[-0.04em] text-xl">MATERA</span>
          <span className="hidden sm:inline text-[10px] tracking-[0.2em] uppercase opacity-80">Surface Works</span>
        </Link>
        <nav className="hidden md:flex items-center gap-8" aria-label="Main">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-[12px] uppercase tracking-[0.16em] whitespace-nowrap cursor-pointer border-b pb-0.5 transition-colors ${
                  isActive ? 'border-current' : 'border-transparent hover:border-current'
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
        <button
          type="button"
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer whitespace-nowrap"
          aria-label={open ? 'Close menu' : 'Open menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
        >
          <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} />
        </button>
      </div>
      {open && (
        <nav className="md:hidden shell pb-6 bg-background-50 flex flex-col" aria-label="Mobile">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className="py-3 border-b border-background-300/60 font-heading font-bold uppercase text-2xl tracking-tight cursor-pointer"
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}